import { useAppServices } from "hook/services";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Alert,
  Snackbar,
  TextField,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Autocomplete,
  Stack,
  Paper,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

// Public site base — listing URLs are always {SITE}/listing/{slug}.
const SITE_URL = "https://aloca.com.au";

// DB listing_description is HTML (<p>, <br>, entities). Emails want clean
// prose, so flatten it to a single readable paragraph on auto-fill.
const stripHtml = (html) =>
  (html || "")
    .replace(/<\s*br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();

// Turn a stored slug ("new-south-wales") into a label ("New South Wales").
const slugToLabel = (s = "") =>
  String(s)
    .split(/[-\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// "City, State" subtitle for a listing's dropdown row (skips blanks).
const listingLocation = (l) =>
  [slugToLabel(l?.address?.city), slugToLabel(l?.address?.state)]
    .filter(Boolean)
    .join(", ");

// Map a listing record (from /listings/get) onto the four email fields.
const listingToFields = (l) => ({
  name: l?.title || "",
  url: l?.slug ? `${SITE_URL}/listing/${l.slug}` : "",
  image: (Array.isArray(l?.images) && l.images[0]) || l?.img_url || "",
  desc: stripHtml(l?.listing_description),
});

// City keys must match the GHL custom-value prefixes used by the campaigns,
// e.g. queensland_1_desc -> {{custom_values.queensland_1_desc}}.
const CITIES = [
  { key: "queensland", label: "Queensland" },
  { key: "sydney", label: "Sydney" },
  { key: "melbourne", label: "Melbourne" },
  { key: "perth", label: "Perth" },
];

const LISTING_FIELDS = ["name", "desc", "image", "url"];
const DEFAULT_LISTINGS = 3;
const MAX_LISTINGS = 3; // weekly email features at most 3 per city

const emptyListing = () => ({ name: "", desc: "", image: "", url: "" });

const emptyCity = () => ({
  listings: Array.from({ length: DEFAULT_LISTINGS }, emptyListing),
  browse_url: "",
  gif_url: "",
  new_count: "",
});

const emptyState = () =>
  CITIES.reduce((acc, c) => {
    acc[c.key] = emptyCity();
    return acc;
  }, {});

// Turn the flat GHL custom-values list into the per-city form state.
const parseCustomValues = (customValues) => {
  const map = {};
  (customValues || []).forEach((cv) => {
    if (cv?.name) map[cv.name.trim().toLowerCase()] = cv.value != null ? cv.value : "";
  });

  const state = {};
  CITIES.forEach(({ key: city }) => {
    // Detect how many listings exist for this city (supports more than 3).
    let maxN = 0;
    Object.keys(map).forEach((k) => {
      const m = k.match(new RegExp(`^${city}_(\\d+)_(?:name|desc|image|url)$`));
      if (m) maxN = Math.max(maxN, parseInt(m[1], 10));
    });
    if (maxN === 0) maxN = DEFAULT_LISTINGS;

    const listings = [];
    for (let i = 1; i <= maxN; i++) {
      listings.push({
        name: map[`${city}_${i}_name`] || "",
        desc: map[`${city}_${i}_desc`] || "",
        image: map[`${city}_${i}_image`] || "",
        url: map[`${city}_${i}_url`] || "",
      });
    }

    state[city] = {
      listings,
      browse_url: map[`${city}_browse_url`] || "",
      gif_url: map[`${city}_gif_url`] || "",
      new_count: map[`${city}_new_count`] || "",
    };
  });
  return state;
};

// Flatten the form state back into [{ name, value }] for the GHL upsert.
const flattenState = (state) => {
  const values = [];
  CITIES.forEach(({ key: city }) => {
    const c = state[city];
    if (!c) return;
    c.listings.forEach((l, idx) => {
      const n = idx + 1;
      LISTING_FIELDS.forEach((f) => {
        values.push({ name: `${city}_${n}_${f}`, value: l[f] || "" });
      });
    });
    values.push({ name: `${city}_browse_url`, value: c.browse_url || "" });
    values.push({ name: `${city}_gif_url`, value: c.gif_url || "" });
    values.push({ name: `${city}_new_count`, value: c.new_count || "" });
  });
  return values;
};

const WeeklyEmails = () => {
  const Service = useAppServices();
  const [state, setState] = useState(emptyState());
  const [cityTab, setCityTab] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Live listing search for the auto-fill dropdown.
  const [listingOptions, setListingOptions] = useState([]);
  const [searchingListings, setSearchingListings] = useState(false);
  const searchDebounce = useRef(null);
  // slug -> images[] cache, so saved listings get their slider gallery too.
  const galleryCache = useRef({});

  const searchListings = (term) => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      try {
        setSearchingListings(true);
        const params = {
          page: 1,
          limit: 20,
          toaster: false,
          startDate: "null",
          endDate: "null",
          search: (term || "").trim(),
          // Only live, public listings — no private, paused or draft.
          publishedOnly: true,
        };
        const query = Object.entries(params)
          .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
          .join("&");
        const { response } = await Service.listing.get({ query });
        const list = response?.success ? response.data || [] : [];
        // Drop listings with no title — they'd render as blank dropdown rows.
        setListingOptions(list.filter((l) => l?.title && l.title.trim()));
      } catch (e) {
        console.error("Error searching listings:", e);
        setListingOptions([]);
      } finally {
        setSearchingListings(false);
      }
    }, 350);
  };

  // Load an initial page so the dropdown isn't empty before the user types.
  useEffect(() => {
    searchListings("");
    return () => searchDebounce.current && clearTimeout(searchDebounce.current);
  }, []);

  const getValues = async () => {
    try {
      setFetching(true);
      const { response } = await Service.super_admin_settings.weekly_custom_values({});
      if (response?.data) {
        setState(parseCustomValues(response.data));
      }
    } catch (e) {
      console.error("Error fetching weekly custom values:", e);
      setError("Failed to load current values from GHL");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    getValues();
  }, []);

  const activeCity = CITIES[cityTab].key;

  // For the currently-open slot, if it was loaded from saved values (so it has
  // a URL but no gallery), fetch the listing by slug to power the image slider.
  useEffect(() => {
    const c = state[activeCity];
    if (!c) return;
    const idx = Math.min(selectedIdx, c.listings.length - 1);
    const l = c.listings[idx];
    if (!l || l._gallery || !l.url) return;
    const slug = (l.url.split("/listing/")[1] || "").split(/[/?#]/)[0].trim();
    if (!slug || galleryCache.current[slug] === "loading") return;

    if (Array.isArray(galleryCache.current[slug])) {
      applyGallery(activeCity, idx, galleryCache.current[slug]);
      return;
    }
    galleryCache.current[slug] = "loading";
    (async () => {
      try {
        const { response } = await Service.listing.get({
          query: `slug=${encodeURIComponent(slug)}`,
        });
        const found = Array.isArray(response?.data) ? response.data[0] : response?.data;
        const imgs = (Array.isArray(found?.images) ? found.images : []).filter(Boolean);
        galleryCache.current[slug] = imgs;
        if (imgs.length) applyGallery(activeCity, idx, imgs);
      } catch (e) {
        delete galleryCache.current[slug];
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCity, selectedIdx, state]);

  const updateListing = (city, idx, field, value) => {
    setState((prev) => {
      const city_ = prev[city];
      const listings = city_.listings.map((l, i) =>
        i === idx ? { ...l, [field]: value } : l
      );
      return { ...prev, [city]: { ...city_, listings } };
    });
  };

  // Pick a real listing from the dropdown → fill all four fields at once,
  // and stash the listing's gallery so the user can swap the featured image.
  const applyListing = (city, idx, listing) => {
    if (!listing) return;
    const fields = listingToFields(listing);
    const gallery = (Array.isArray(listing?.images) ? listing.images : []).filter(Boolean);
    setState((prev) => {
      const city_ = prev[city];
      const listings = city_.listings.map((l, i) =>
        i === idx ? { ...l, ...fields, _gallery: gallery } : l
      );
      return { ...prev, [city]: { ...city_, listings } };
    });
  };

  // Attach a fetched gallery to a slot (only if it doesn't already have one).
  const applyGallery = (city, idx, imgs) => {
    setState((prev) => {
      const city_ = prev[city];
      const cur = city_?.listings?.[idx];
      if (!cur || cur._gallery) return prev;
      const listings = city_.listings.map((l, i) =>
        i === idx ? { ...l, _gallery: imgs } : l
      );
      return { ...prev, [city]: { ...city_, listings } };
    });
  };

  const updateCityField = (city, field, value) => {
    setState((prev) => ({ ...prev, [city]: { ...prev[city], [field]: value } }));
  };

  const removeListing = (city, idx) => {
    setState((prev) => ({
      ...prev,
      [city]: {
        ...prev[city],
        listings: prev[city].listings.filter((_, i) => i !== idx),
      },
    }));
    setSelectedIdx((cur) => (cur >= idx && cur > 0 ? cur - 1 : cur));
  };

  // Add an empty slot (capped at MAX_LISTINGS) and jump to it.
  const addListing = (city) => {
    setState((prev) => {
      const city_ = prev[city];
      if (city_.listings.length >= MAX_LISTINGS) return prev;
      return { ...prev, [city]: { ...city_, listings: [...city_.listings, emptyListing()] } };
    });
    setSelectedIdx(() => {
      const len = state[city]?.listings?.length || 0;
      return Math.min(len, MAX_LISTINGS - 1);
    });
  };

  const changeCity = (v) => {
    setCityTab(v);
    setSelectedIdx(0);
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      const values = flattenState(state);
      const { response, error: apiError } = await Service.super_admin_settings.update_weekly_custom_values({
        payload: { values },
      });
      if (apiError) {
        setError(apiError?.message || "Failed to update GHL");
        return;
      }
      const d = response?.data || {};
      if (d.failed > 0) {
        setError(`Updated ${d.updated}, created ${d.created}, but ${d.failed} failed. Check the listing URLs/values.`);
      } else {
        setSuccess(`Saved to GHL — updated ${d.updated}, created ${d.created}.`);
      }
    } catch (e) {
      console.error("Error saving weekly custom values:", e);
      setError("Failed to save to GHL");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
  };

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const city = state[activeCity];
  const safeIdx = Math.min(selectedIdx, city.listings.length - 1);
  const selected = city.listings[safeIdx];

  // The picked listing's full gallery — shown as a clickable grid so the
  // admin can feature any image. Falls back to the single saved image.
  const gallery =
    selected._gallery && selected._gallery.length
      ? selected._gallery
      : selected.image
      ? [selected.image]
      : [];

  const listingPicker = (
    <Autocomplete
      options={listingOptions}
      loading={searchingListings}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o?.title || "")}
      isOptionEqualToValue={(o, v) => o?._id === v?._id}
      value={null}
      blurOnSelect
      clearOnBlur
      onChange={(_e, value) => applyListing(activeCity, safeIdx, value)}
      onInputChange={(_e, value, reason) => {
        if (reason === "input") searchListings(value);
      }}
      renderOption={(props, option) => {
        const loc = listingLocation(option);
        return (
          <Box component="li" {...props} key={option._id} sx={{ display: "block !important", py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {option.title}
            </Typography>
            {loc ? (
              <Typography variant="caption" color="text.secondary">
                {loc}
              </Typography>
            ) : null}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search a listing to auto-fill"
          placeholder="Type a listing name…"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon fontSize="small" sx={{ color: "text.disabled", ml: 0.5, mr: 0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {searchingListings ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  return (
    <>
      <Card
        sx={{
          mt: "2rem",
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        }}
      >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
          Weekly Email Listings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick a slot on the left, search a listing, and Name · URL · Image · Description fill in
          automatically. Edits push straight to GHL custom values.
        </Typography>
      </Box>

      <Tabs
        value={cityTab}
        onChange={(e, v) => changeCity(v)}
        variant="fullWidth"
        TabIndicatorProps={{ style: { display: "none" } }}
        sx={{
          mb: 3,
          minHeight: 46,
          bgcolor: "grey.100",
          borderRadius: 2,
          p: 0.5,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 1.5,
            minHeight: 38,
            color: "text.secondary",
            transition: "all .15s ease",
          },
          "& .Mui-selected": {
            bgcolor: "#3F98EF",
            color: "#fff !important",
            boxShadow: "0 2px 8px rgba(63,152,239,0.45)",
          },
        }}
      >
        {CITIES.map((c) => (
          <Tab key={c.key} label={c.label} disableRipple />
        ))}
      </Tabs>

      <Grid container spacing={3}>
        {/* LEFT — listing slot rail */}
        <Grid item xs={12} md={4} lg={3.5}>
          <Typography variant="overline" color="text.secondary" sx={{ pl: 0.5 }}>
            Featured listings
          </Typography>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            {city.listings.map((l, idx) => {
              const active = idx === safeIdx;
              return (
                <Paper
                  key={idx}
                  elevation={0}
                  onClick={() => setSelectedIdx(idx)}
                  sx={{
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    cursor: "pointer",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: active ? "#3F98EF" : "divider",
                    bgcolor: active ? "rgba(63,152,239,0.08)" : "background.paper",
                    boxShadow: active ? "0 0 0 1px rgba(63,152,239,0.35)" : "none",
                    transition: "all .15s ease",
                    "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      bgcolor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {l.image ? (
                      <Box
                        component="img"
                        src={l.image}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onLoad={(e) => { e.target.style.display = "block"; }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <ImageOutlinedIcon fontSize="small" sx={{ color: "grey.400" }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                      Listing {idx + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 600, color: l.name ? "text.primary" : "text.disabled" }}
                    >
                      {l.name || "Empty — pick a listing"}
                    </Typography>
                  </Box>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); removeListing(activeCity, idx); }}
                        disabled={city.listings.length <= 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Paper>
              );
            })}
          </Stack>
          {city.listings.length < MAX_LISTINGS ? (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              fullWidth
              disableFocusRipple
              onClick={() => addListing(activeCity)}
              sx={{
                mt: 1.5,
                textTransform: "none",
                borderRadius: 2,
                borderStyle: "dashed",
                borderColor: "#3F98EF",
                color: "#3F98EF",
                fontWeight: 600,
                "&:hover": { borderColor: "#3F98EF", borderStyle: "dashed", bgcolor: "rgba(63,152,239,0.06)" },
              }}
            >
              Add Listing ({city.listings.length}/{MAX_LISTINGS})
            </Button>
          ) : null}
        </Grid>

        {/* RIGHT — selected listing editor */}
        <Grid item xs={12} md={8} lg={8.5}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                Listing {safeIdx + 1}
              </Typography>

              <Box sx={{ mt: 1, mb: 2.5 }}>{listingPicker}</Box>

              {selected.image ? (
                <Box
                  component="img"
                  src={selected.image}
                  alt="preview"
                  loading="eager"
                  decoding="async"
                  sx={{
                    width: "100%",
                    height: 360,
                    objectFit: "contain",
                    objectPosition: "center",
                    borderRadius: 2,
                    bgcolor: "grey.100",
                    border: "1px solid",
                    borderColor: "divider",
                    display: "block",
                    mb: 2,
                  }}
                  onLoad={(e) => { e.target.style.display = "block"; }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : null}

              {gallery.length > 1 ? (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Gallery — click an image to feature it
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                      gap: 1,
                    }}
                  >
                    {gallery.map((img) => {
                      const chosen = img === selected.image;
                      return (
                        <Box
                          key={img}
                          component="img"
                          src={img}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onClick={() => updateListing(activeCity, safeIdx, "image", img)}
                          onError={(e) => { e.target.style.visibility = "hidden"; }}
                          sx={{
                            width: "100%",
                            height: 72,
                            objectFit: "cover",
                            borderRadius: 1.5,
                            cursor: "pointer",
                            border: "2px solid",
                            borderColor: chosen ? "#3F98EF" : "transparent",
                            outline: "1px solid",
                            outlineColor: chosen ? "#3F98EF" : "divider",
                            boxShadow: chosen ? "0 0 0 2px rgba(63,152,239,0.35)" : "none",
                            opacity: chosen ? 1 : 0.85,
                            transition: "all .12s ease",
                            "&:hover": { opacity: 1, borderColor: "#3F98EF" },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ) : null}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Listing Name / Title"
                    value={selected.name}
                    onChange={(e) => updateListing(activeCity, safeIdx, "name", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Listing URL"
                    value={selected.url}
                    onChange={(e) => updateListing(activeCity, safeIdx, "url", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Image URL"
                    value={selected.image}
                    onChange={(e) => updateListing(activeCity, safeIdx, "image", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Description"
                    value={selected.desc}
                    onChange={(e) => updateListing(activeCity, safeIdx, "desc", e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Email settings + save */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            {CITIES[cityTab].label} — Email Settings
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Browse URL"
            value={city.browse_url}
            onChange={(e) => updateCityField(activeCity, "browse_url", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label="GIF URL"
            value={city.gif_url}
            onChange={(e) => updateCityField(activeCity, "gif_url", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="New Listings Count"
            value={city.new_count}
            onChange={(e) => updateCityField(activeCity, "new_count", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            onClick={saveAll}
            disabled={saving}
            fullWidth
            size="large"
            style={{ background: "#3F98EF", color: "white", marginTop: "1.5rem" }}
            variant="contained"
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : "Save All Cities to GHL"}
          </Button>
        </Grid>
      </Grid>
      </Card>

      <Snackbar open={!!error} autoHideDuration={8000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WeeklyEmails;
